import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "../utils/APIResponse";

export enum HealthTypes {
  Connecting = "connecting",
  Disconnected = "disconnected",
  Connected = "connected",
  Disconnecting = "disconnecting",
  Unauthorized = "unauthorized",
}

export interface ServiceStatus {
  status: "healthy" | "unhealthy" | "degraded";
  responseTime?: number;
  message?: string;
}

export interface HealthStatus {
  dbState: HealthTypes;
  status?: string;
  services?: {
    [key: string]: ServiceStatus; // e.g., "database", "redis", "s3"
  };
  uptime?: number; // in seconds
  version?: string;
  timestamp?: string;
  environment?: string;
}

export class HealthService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/health";
  }

  async getDatabaseStatus(
    config?: AxiosRequestConfig
  ): Promise<{ data: HealthStatus; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<HealthStatus>>(
        `${this.baseUrl}/status`,
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Error fetching organization overview:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch organization overview.";
      throw new Error(errorMessage);
    }
  }

  async getHealthStatus(
    config?: AxiosRequestConfig
  ): Promise<{ data: HealthStatus; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<HealthStatus>>(
        `${this.baseUrl}/`,
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Error fetching health status:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch health status.";
      throw new Error(errorMessage);
    }
  }

  async getLiveness(
    config?: AxiosRequestConfig
  ): Promise<{ data: { status: string }; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<{ status: string }>>(
        `${this.baseUrl}/live`,
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Error fetching liveness probe:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch liveness probe.";
      throw new Error(errorMessage);
    }
  }

  async getReadiness(
    config?: AxiosRequestConfig
  ): Promise<{ data: { status: string }; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<{ status: string }>>(
        `${this.baseUrl}/ready`,
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Error fetching readiness probe:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch readiness probe.";
      throw new Error(errorMessage);
    }
  }
}

export const healthService = new HealthService();
